import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Cpu,
  ArrowRight,
  ArrowLeft,
  Check,
  Zap,
  Brain,
  DollarSign,
  Star,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Loader2
} from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import Button from '../../components/Button';
import api from '../../services/api';

export default function StepModel() {
  const { goNext, goPrev } = useOutletContext();
  const { currentAgent, updateAgentModel, isLoading, error, clearError } = useAgent();
  const [expandedModel, setExpandedModel] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [selectedModel, setSelectedModel] = useState(currentAgent?.model || 'openai/gpt-4o-mini');
  const [llmModels, setLlmModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(true);

  // Fetch models from backend API
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const result = await api.getAvailableModels();
        setLlmModels(result.models || []);
      } catch (err) {
        console.error('Failed to fetch models:', err);
      } finally {
        setLoadingModels(false);
      }
    };
    fetchModels();
  }, []);

  // Update selected model when currentAgent loads (for resume flow)
  useEffect(() => {
    if (currentAgent?.model) {
      setSelectedModel(currentAgent.model);
    }
  }, [currentAgent?.model]);

  const handleSelectModel = (modelId) => {
    setSelectedModel(modelId);
    if (error) clearError();
  };

  const handleContinue = async () => {
    try {
      await updateAgentModel({ model: selectedModel });
      goNext();
    } catch (err) {
      console.error('Failed to save model:', err);
    }
  };

  const toggleExpand = (modelId) => {
    setExpandedModel(expandedModel === modelId ? null : modelId);
  };

  const getSpeedBars = (speed) => {
    switch (speed) {
      case 'Very Fast': return 4;
      case 'Fast': return 3;
      case 'Medium': return 2;
      default: return 1;
    }
  };

  const getSmartDots = (intel) => {
    switch (intel) {
      case 'Excellent': return 5;
      case 'Very Good': return 4;
      case 'Good': return 3;
      default: return 2;
    }
  };

  const getCostColor = (cost) => {
    switch (cost) {
      case 'Very Cheap': return 'text-green-600';
      case 'Cheaper': return 'text-green-600';
      case 'Medium': return 'text-amber-600';
      case 'Premium': return 'text-purple-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="p-8 max-w-3xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Cpu className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Choose Your AI Brain</h1>
            <p className="text-text-secondary">Select the AI model that powers your assistant</p>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <button
        onClick={() => setShowHelp(!showHelp)}
        className="w-full mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100 text-left
                   hover:bg-blue-100 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-blue-800">What is an AI model?</span>
          </div>
          {showHelp ? (
            <ChevronUp className="w-5 h-5 text-blue-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-blue-600" />
          )}
        </div>

        {showHelp && (
          <div className="mt-3 text-sm text-blue-700 space-y-2">
            <p>
              <strong>Think of it like choosing a brain for your AI assistant.</strong> Different models
              have different strengths - some are faster, some are smarter, and some cost less.
            </p>
            <p>
              <strong>Speed</strong> = How quickly your customers get a response<br />
              <strong>Smart</strong> = How well it understands complex questions<br />
              <strong>Cost</strong> = Relative cost compared to other models
            </p>
            <p className="text-blue-600 font-medium">
              Not sure? We recommend GPT-4o Mini - it's the best balance of quality and price for most businesses.
            </p>
          </div>
        )}
      </button>

      {/* Models List */}
      {loadingModels ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-primary animate-spin" />
          <span className="ml-3 text-text-muted">Loading available models...</span>
        </div>
      ) : (
      <div className="space-y-3">
        {llmModels.map((model, index) => {
          const isSelected = selectedModel === model.id;
          const isExpanded = expandedModel === model.id;
          const speedBars = getSpeedBars(model.speed);
          const smartDots = getSmartDots(model.intelligence);

          return (
            <div
              key={model.id}
              className={`
                card overflow-hidden transition-all duration-300 cursor-pointer
                animate-slide-up
                ${isSelected
                  ? 'ring-2 ring-primary border-primary bg-primary/5'
                  : 'hover:border-gray-300 hover:shadow-md'
                }
              `}
              style={{ animationDelay: `${index * 50}ms` }}
              onClick={() => handleSelectModel(model.id)}
            >
              {/* Main Row */}
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Selection Indicator */}
                  <div className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5
                    transition-colors flex-shrink-0
                    ${isSelected
                      ? 'border-primary bg-primary'
                      : 'border-gray-300'
                    }
                  `}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title Row */}
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-semibold text-text-primary">{model.name}</h3>
                      <span className="text-xs text-text-muted">by {model.provider}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${model.badgeColor}`}>
                        {model.badge}
                      </span>
                      {model.recommended && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          <Star className="w-3 h-3" /> Recommended
                        </span>
                      )}
                    </div>

                    {/* Short Description */}
                    <p className="text-sm text-text-secondary mb-2">
                      {model.shortDescription || model.description}
                    </p>

                    {/* Quick Stats Row */}
                    <div className="flex items-center gap-4 text-sm">
                      {/* Speed */}
                      <div className="flex items-center gap-1.5">
                        <Zap className="w-4 h-4 text-amber-500" />
                        <span className="text-text-muted">Speed:</span>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4].map((bar) => (
                            <div
                              key={bar}
                              className={`w-1 h-3 rounded-sm ${
                                bar <= speedBars ? 'bg-amber-500' : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Intelligence */}
                      <div className="flex items-center gap-1.5">
                        <Brain className="w-4 h-4 text-blue-500" />
                        <span className="text-text-muted">Smart:</span>
                        <div className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((dot) => (
                            <div
                              key={dot}
                              className={`w-1.5 h-1.5 rounded-full ${
                                dot <= smartDots ? 'bg-blue-500' : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Cost */}
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4 text-green-500" />
                        <span className={`font-medium ${getCostColor(model.cost)}`}>
                          {model.cost || 'Medium'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expand Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(model.id);
                    }}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-text-muted" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-text-muted" />
                    )}
                  </button>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-0 border-t border-border">
                  <div className="pt-3 grid md:grid-cols-2 gap-4">
                    {/* Strengths */}
                    <div>
                      <h4 className="text-sm font-medium text-text-primary mb-2">What it's good at:</h4>
                      <ul className="space-y-1">
                        {model.strengths?.map((strength, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-text-secondary">
                            <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Best For */}
                    <div>
                      <h4 className="text-sm font-medium text-text-primary mb-1">Best for:</h4>
                      <p className="text-sm text-text-secondary">{model.bestFor}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
          <button onClick={clearError} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Selected Summary */}
      {selectedModel && (
        <div className="mt-6 p-4 bg-primary/5 rounded-xl border border-primary/10">
          <div className="flex items-center gap-2">
            <Check className="w-5 h-5 text-primary" />
            <span className="text-sm text-text-secondary">Selected:</span>
            <span className="font-semibold text-primary">
              {llmModels.find(m => m.id === selectedModel)?.name}
            </span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          icon={isLoading ? Loader2 : ArrowRight}
          iconPosition="right"
          onClick={handleContinue}
          disabled={isLoading}
          className={isLoading ? '[&_svg]:animate-spin' : ''}
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}